// The cloud reveal's staging backbone (cloud-ftue task 6.1).
//
// The cloud reveal is read-mostly. The boat runs the scan/analysis and mirrors
// its whole `SetupSession` up as a self-healing snapshot (design D5); the cloud
// reveal OBSERVES that snapshot (+ the live telemetry + the source-analysis
// lane) and DERIVES a staged unveiling from it — it never owns an action
// stream. This module is that pure, snapshot-derived stage machine. It holds
// two load-bearing FTUE invariants:
//
//   1. NO SILENT COMPLETION — a scan that finds
//      nothing and a failed analysis are FIRST-CLASS blocking states that
//      demand an explicit user choice, never a quiet fast-forward to the
//      dashboard. Encoded here as {@link isNoDevices} / {@link isAnalysisError},
//      surfaced by the reveal as blocking screens (tasks 7.5).
//   2. QUALITY FLOOR ({@link devicesSummary} / {@link candidateLabel}) — a
//      modest boat still reads as understood; copy never shows a raw zero
//      (tasks 7.2). Honest progress, never a fake spinner (task 7.1).
//
// Everything here is pure (no React/DOM/fetch) so it is unit-testable in
// isolation.
//
// The Gateways stage's two determinate progress bars (lib/revealProgress.ts) are
// compatible with the "no silent/timed completion" rule: each is capped below 100%
// and only a REAL terminus (scanSettled / analysis ready) completes it, with the
// streamed counts/list alongside as the source of truth.

/** The three composed reveal stages (ftue-reveal-flow-cleanup ⑤ dropped the
 *  terminal 'live' stage — the reveal now completes into the boat's dashboard). */
export type RevealStage = 'found' | 'aboard' | 'watch';

// Order (reveal-gateways-onetab): the gateway scan AND the primary-gateway
// decision are now ONE stage — 'found' (step-label "Gateways") — rendered as a
// single progressive panel (scan → determine the primary → the pick). The former
// separate 'connect' stage is folded into 'found'; what follows is the device
// inventory ('aboard'), then what we'll watch ('watch', the terminal stage).
// Advancing past 'found' is GATED on the primary pick being determined (see
// {@link dataStageFor}). Completing the 'watch' stage navigates to the boat's
// dashboard — there is no in-reveal 'live' stage (ftue-reveal-flow-cleanup ⑤).
export const REVEAL_STAGES: readonly RevealStage[] = ['found', 'aboard', 'watch'] as const;

/**
 * The boat-side setup phases (core `SETUP_PHASES`, mirrored here so the web
 * package stays self-contained — the snapshot ships `phase` as a plain string).
 * Order is load-bearing: the reveal derives "scan complete" from
 * `phaseIndex >= phaseIndex('device_inventory')`.
 */
export const SETUP_PHASES = [
  'activation_parallel',
  'scan_scope',
  'gateway_scan',
  'connection_validation',
  'device_inventory',
  'config_harvest',
  'user_identification',
  'alarm_recommendation',
  'user_confirmation',
  'ruleset_apply',
  'complete',
  'source_analysis',
  'source_confirmation',
  'source_apply',
] as const;

/** Index of `phase` in {@link SETUP_PHASES}, or -1 when unknown/absent. */
export function phaseIndex(phase: string | null): number {
  if (phase === null) return -1;
  return SETUP_PHASES.indexOf(phase as (typeof SETUP_PHASES)[number]);
}

const DEVICE_INVENTORY_INDEX = SETUP_PHASES.indexOf('device_inventory');

/**
 * "The scan has produced its terminus." The orchestrator halts a magic session
 * at `device_inventory` and emits `setup_scan_complete` (core types.ts
 * MAGIC_STOP_AFTER_PHASE), so any phase at or past it — or a `complete` session
 * — means discovery is settled and the reveal may leave the live "found" beat.
 */
export function scanSettled(input: RevealInput): boolean {
  if (input.status === 'complete') return true;
  return phaseIndex(input.phase) >= DEVICE_INVENTORY_INDEX;
}

/** The source-analysis lane's readiness (mirrors `useSourceAnalysis` kinds). */
export type AnalysisReadiness = 'loading' | 'none' | 'ready' | 'error';

/**
 * The plain, serializable inputs the stage machine reasons over — assembled by
 * the reveal page from the mirrored snapshot, the device inventory, and the
 * source-analysis lane. Deliberately small; the heavy payloads stay in the
 * page's React state.
 */
export interface RevealInput {
  /** GET /v1/boats/:id/setup returned a snapshot (false ⇒ 404 no_setup yet). */
  hasSnapshot: boolean;
  /** `session.phase` from the latest snapshot, or null before one arrives. */
  phase: string | null;
  /** `session.status`, or null before a snapshot arrives. */
  status: 'active' | 'complete' | 'error' | null;
  /** `session.candidates.length` — gateways/sources discovered (task 7.1). */
  candidateCount: number;
  /** Device-inventory count from `useBoatDevices` (0 while loading). */
  deviceCount: number;
  /** Source-analysis lane readiness. */
  analysis: AnalysisReadiness;
  /**
   * The analysis is actively being computed (loading, or the long 404='none'
   * compute window while the boat is reachable). Holds the advance gate so the
   * stepper can't jump past the decision while it determines (D2). Absent/false
   * means "not in flight" — settled, or genuinely can't run (offline).
   */
  analysisInFlight?: boolean;
  /** `session.recommendations.length` — suggested alarms in the snapshot. */
  alarmCount: number;
  /** True once a `setup_apply` command has completed (command_status). */
  applied: boolean;
}

/**
 * NO-GATEWAY HARD STOP (reveal-gateways-onetab D3): the scan reached its terminus
 * but found NO gateway at all. Neomaris cannot observe the boat without one, so
 * this is a HARD stop — the reveal shows a requirement screen offering ONLY a
 * rescan (no "continue anyway"). Keyed purely on the gateway prerequisite
 * (candidate count), independent of device count, and takes PRECEDENCE over
 * {@link isNoDevices}.
 */
export function isNoGateways(input: RevealInput): boolean {
  return scanSettled(input) && input.candidateCount === 0;
}

/**
 * The scan settled with a gateway present but NO devices behind it yet — a
 * working-but-quiet boat, not a broken one (reveal-gateways-onetab D3). Distinct
 * from {@link isNoGateways} (which wins when there is no gateway at all): a quiet
 * boat is NOT blocked — it flows through to the device stage's friendly
 * "gateways connected, still reading the devices" floor ({@link devicesSummary}).
 * Kept as a predicate (the no-gateways-vs-no-devices truth table) and mutually
 * exclusive with isNoGateways via the `candidateCount > 0` guard.
 */
export function isNoDevices(input: RevealInput): boolean {
  return scanSettled(input) && input.candidateCount > 0 && input.deviceCount === 0;
}

/**
 * NO SILENT COMPLETION (task 7.5): the source analysis failed (offline /
 * timeout / LLM error). Blocking — offer retry / explicit skip.
 */
export function isAnalysisError(input: RevealInput): boolean {
  return input.analysis === 'error';
}

/**
 * The furthest stage the data currently supports — the reveal's auto-advance
 * ceiling. The page lets the user walk forward up to here; a later stage the
 * user steps into shows honest progress until its data lands (design D7: "fills
 * later stages in as their results land", never a stalled spinner).
 *
 * 'watch' is the terminal stage (ftue-reveal-flow-cleanup ⑤): completing it
 * navigates to the boat's dashboard rather than an in-reveal 'live' stage.
 */
export function dataStageFor(input: RevealInput): RevealStage {
  let stage: RevealStage = 'found';
  // Advancing past the merged Gateways stage ('found') is GATED on the primary
  // pick being DETERMINED, not on scan-settle alone (reveal-gateways-onetab D2).
  // The ceiling rises to 'aboard' (Devices) only when discovery has settled with
  // a gateway AND the analysis has landed:
  //   - 'ready'   → the pick (incl. the deterministic 'degraded' result, which
  //                 surfaces through the lane as 'ready') has landed → clears.
  //   - 'none'    → no writable source to choose / analysis can't run — nothing
  //                 to determine, the default plan stands → clears (don't strand).
  //   - 'loading' → still determining → HOLDS at 'found' (Continue disabled).
  //   - 'error'   → does NOT clear; the page routes this to the analysis-error
  //                 blocking screen, not a stuck button.
  // candidateCount === 0 is the no-gateway hard stop ({@link isNoGateways}); it
  // keeps the ceiling at 'found' so there is nothing to advance into.
  // The pick is "determined enough" to advance when the analysis is ready, or when
  // it is 'none' AND not actively in flight (offline / nothing to determine). While
  // it is in flight (loading, or the long 404='none' compute window) the gate HOLDS
  // so the stepper can't jump past the decision (D2).
  const pickDetermined =
    input.analysis === 'ready' || (input.analysis === 'none' && input.analysisInFlight !== true);
  if (scanSettled(input) && input.candidateCount > 0 && pickDetermined) {
    stage = 'aboard';
  }
  // Watch: suggested alarms are present in the snapshot, OR the boat is fully set
  // up (applied/complete) — 'watch' is the terminal stage; completing it exits to
  // the dashboard (ftue-reveal-flow-cleanup ⑤).
  if (input.alarmCount > 0 || input.applied || input.status === 'complete') stage = 'watch';
  return stage;
}

/**
 * Should the reveal poll the source-analysis lane as a fallback for a lost/late
 * `source_analysis_ready` WS push? Only while the pick is genuinely IN FLIGHT:
 * the lane is still computing ('none'/'loading'), the scan has settled, and there
 * is a gateway to choose over a live session. Resolves false the instant the lane
 * lands (ready/error) or the candidate/session preconditions disappear — which
 * tears the interval down.
 *
 * The single WS notify (`source_analysis_ready`) is the only thing that
 * re-fetches the lane (useSourceAnalysis); a dropped/coalesced push would
 * otherwise strand the determining beat at 404 with Continue disabled until a
 * manual refresh re-bootstraps the GET. A bounded poll closes that gap.
 *
 * Deliberately NOT gated on the client `presence` heuristic. `presence` reads
 * stale-offline when the boat is actually connected (last_contacted_at lags the
 * live WS) — exactly while the analysis is in flight — so gating the poll on it
 * disables the fallback precisely when it is needed (the same hazard the
 * Devices→Alarms apply already refuses to gate on). The GET is cheap and the
 * determining beat is bounded by its watchdog, so polling a genuinely-offline
 * boat just 404s harmlessly until the user skips.
 */
export function shouldPollAnalysis(args: {
  analysis: AnalysisReadiness;
  settled: boolean;
  candidateCount: number;
  hasSession: boolean;
}): boolean {
  if (args.analysis !== 'none' && args.analysis !== 'loading') return false;
  return args.settled && args.candidateCount > 0 && args.hasSession;
}

/**
 * Should the reveal poll the SETUP-snapshot lane as a fallback for a missed
 * snapshot frame? Only while we're still waiting for the scan to settle — a
 * loaded (`ready`) or still-mirroring (`waiting`) lane whose phase hasn't reached
 * `device_inventory`.
 *
 * `useBoatSetup` updates only on live WS snapshot frames plus the mount-time
 * bootstrap GET — it has NO periodic re-read of its own. A snapshot the boat
 * mirrors in the gap between that bootstrap GET and the WS subscription actually
 * attaching to the bus is lost (the fan-out has no replay), so the Gateways stage
 * strands on a stale PRE-SETTLE snapshot (e.g. still `connection_validation`)
 * while the boat has already advanced — the "have to refresh to move to the next
 * stage" bug. A bounded poll re-GETs the authoritative snapshot so a missed
 * settle-transition frame can't hang the scanning beat; it resolves false the
 * instant the phase reaches `device_inventory` (the pick beat's own analysis poll
 * takes over from there). Mirrors {@link shouldPollAnalysis}. Not gated on
 * `loading`/`error` — there's nothing to re-read against until the lane holds a
 * snapshot (or the documented 404 `waiting`).
 */
export function shouldPollSetupSettle(args: {
  setup: 'loading' | 'waiting' | 'ready' | 'error';
  settled: boolean;
}): boolean {
  if (args.setup !== 'ready' && args.setup !== 'waiting') return false;
  return !args.settled;
}

export function stageIndex(stage: RevealStage): number {
  return REVEAL_STAGES.indexOf(stage);
}

export function nextStage(stage: RevealStage): RevealStage | null {
  const i = stageIndex(stage);
  return i >= 0 && i < REVEAL_STAGES.length - 1 ? REVEAL_STAGES[i + 1]! : null;
}

export function prevStage(stage: RevealStage): RevealStage | null {
  const i = stageIndex(stage);
  return i > 0 ? REVEAL_STAGES[i - 1]! : null;
}

/**
 * Is the data for `stage` ready (so the user can meaningfully advance INTO it)?
 * `found` is always permitted — it opens the reveal.
 */
export function isStageReady(stage: RevealStage, input: RevealInput): boolean {
  const ceiling = dataStageFor(input);
  if (stage === 'found') return true;
  return stageIndex(stage) <= stageIndex(ceiling);
}

// ---------- Copy helpers (pure; honest + sparse-floor framing) ----------

export function stageTitle(stage: RevealStage): string {
  switch (stage) {
    case 'found':
      return 'Your gateways and main connection';
    case 'aboard':
      return "What's aboard";
    case 'watch':
      return "What we'll watch";
  }
}

export function stageStepLabel(stage: RevealStage): string {
  switch (stage) {
    case 'found':
      return 'Gateways';
    case 'aboard':
      return 'Devices';
    case 'watch':
      return 'Watching';
  }
}

/**
 * Honest cold-start progress (task 7.1) — real counts from the snapshot, never
 * a timed fake.
 */
export function foundProgressLabel(candidateCount: number, settled: boolean): string {
  if (settled) {
    if (candidateCount === 0) return 'Finishing the first sweep of your network…';
    return candidateCount === 1
      ? 'Found 1 gateway on your network.'
      : `Found ${candidateCount} gateways on your network.`;
  }
  if (candidateCount === 0) return 'Listening on your network for gateways…';
  return candidateCount === 1
    ? 'Found 1 gateway so far…'
    : `Found ${candidateCount} gateways so far…`;
}

/**
 * Quality floor for the "aboard" stage (task 7.2) — a modest boat still reads as
 * understood, and copy never shows a raw zero.
 */
export function devicesSummary(
  deviceCount: number,
  candidateCount: number
): { title: string; sub: string } {
  if (deviceCount > 0) {
    return {
      title: deviceCount === 1 ? '1 device aboard' : `${deviceCount} devices aboard`,
      sub: 'Here’s the equipment we recognised on your network.',
    };
  }
  if (candidateCount > 0) {
    return {
      title: candidateCount === 1 ? 'Your gateway is connected' : 'Your gateways are connected',
      sub: 'We’re still reading the devices behind them — they’ll appear here as they report in.',
    };
  }
  return {
    title: 'Your boat is connected',
    sub: 'We’re getting to know what’s aboard.',
  };
}

/**
 * Quality floor / confidence framing for the "watch" stage (tasks 7.2 / 7.3).
 * Separates the deterministic safety floor from AI-suggested additions so the
 * copy is honest about which alarms are confident vs proposed.
 */
export function alarmsSummary(total: number, aiCount: number): { title: string; sub: string } {
  if (total === 0) {
    return {
      title: 'Keeping an eye out',
      sub: 'We’ll start watching for trouble as soon as we’ve learned your devices’ normal ranges.',
    };
  }
  const base = total - aiCount;
  const title = total === 1 ? '1 alarm ready' : `${total} alarms ready`;
  if (aiCount > 0 && base > 0) {
    return {
      title,
      sub: `${base} from your equipment, plus ${aiCount} we think might help — review the suggested ones before turning them on.`,
    };
  }
  if (aiCount > 0) {
    return {
      title,
      sub: 'These are suggestions — have a look and pick the ones that fit your boat.',
    };
  }
  return {
    title,
    sub: 'These come straight from the equipment we found aboard.',
  };
}

/**
 * The owner-driven setup verbs the reveal relays to the boat. `record-decisions`
 * and `apply` are the reveal-alarm-review arming pair (§5 of the contract):
 * record-decisions persists the per-alarm accept/reject choices, then the
 * existing `apply` (setup_apply) compiles them. `harvest-credentials` is the
 * config-harvest credentials round trip (B3, the existing
 * `setup_harvest_credentials` verb) — a non-blocking sub-beat of the Watch
 * stage, never gating "Start watching".
 */
export type SetupRelayVerb =
  | 'reanalyze'
  | 'rescan'
  | 'record-decisions'
  | 'apply'
  | 'harvest-credentials';

/**
 * Copy for a failed relay POST. The relay 409s when the cloud cannot reach the
 * boat (genuinely offline, even single-host), and a transport blip reads the
 * same to the user — so the honest copy is "we couldn't reach your boat" plus
 * a retry, never a silent swallow (task 7.5).
 */
export function relayFailureMessage(verb: SetupRelayVerb): string {
  switch (verb) {
    case 'rescan':
      return 'We couldn’t reach your boat to restart the scan. Check that it’s online, then try again.';
    case 'reanalyze':
      return 'We couldn’t reach your boat to start the network analysis. Check that it’s online, then try again.';
    case 'record-decisions':
      return 'We couldn’t reach your boat to save your alarm choices. Check that it’s online, then try again.';
    case 'apply':
      return 'We couldn’t reach your boat to start watching. Check that it’s online, then try again.';
    case 'harvest-credentials':
      return 'We couldn’t reach your boat to save that login. Check that it’s online, then try again.';
  }
}

// ---------- Analysis-kick auto-retry (task B5) ----------

/**
 * Delays for the bounded auto-retry of a `boat_offline` analysis kick. Right
 * after a claim the boat's own cloud WS is typically still in its (~30 s)
 * reconnect backoff, so the scan-settle auto-kick's reanalyze relay
 * predictably 409s `boat_offline` even though the boat is seconds from coming
 * back. Three silent retries spanning that window (10/20/30 s — a minute
 * total) bridge the gap; a boat still unreachable after that gets the banner.
 */
export const ANALYSIS_KICK_RETRY_DELAYS_MS: readonly number[] = [10_000, 20_000, 30_000];

/**
 * Decide whether a failed analysis kick is silently retried, and after what
 * delay. `error` is the value the `api()` client throws (an Error carrying
 * `status` + the parsed response `body` — the same code-field shape
 * `apiErrorCode` in lib/marineToolkit.ts reads); `failedAttempts` counts the
 * consecutive failed kicks INCLUDING the one that just failed (1-based).
 * Returns the ms to wait before re-kicking, or null to surface the existing
 * relay-failure banner:
 *
 *   - ONLY the relay's 409 `boat_offline` retries — the one failure that
 *     predictably self-heals (the boat's WS reconnect after a claim). Every
 *     other failure (transport blip, auth, 5xx) banners immediately,
 *     preserving the kick latch's no-auto-loop guarantee (see
 *     `analysisKickedRef` in pages/Reveal.tsx) for non-offline errors.
 *   - Bounded by {@link ANALYSIS_KICK_RETRY_DELAYS_MS}: the fourth
 *     consecutive offline failure banners too.
 */
export function analysisKickRetryDelayMs(error: unknown, failedAttempts: number): number | null {
  if (!isBoatOfflineRelayError(error)) return null;
  return ANALYSIS_KICK_RETRY_DELAYS_MS[failedAttempts - 1] ?? null;
}

/** The setup relay's "no fresh contact anywhere" rejection: HTTP 409 with a
 *  JSON body whose `error` code is `boat_offline` (cloud setup routes). Shared
 *  with the Alarms ack/silence buttons (UI-2), which key on the same shape. */
export function isBoatOfflineRelayError(error: unknown): boolean {
  if (error === null || typeof error !== 'object') return false;
  const { status, body } = error as { status?: unknown; body?: unknown };
  if (status !== 409 || body === null || typeof body !== 'object') return false;
  return (body as Record<string, unknown>).error === 'boat_offline';
}

// ---------- Candidate labels (task 7.2 — never blank) ----------

/** The discovery fields of a `SetupSourceCandidate` the found stage reads. */
export interface RevealCandidate {
  id?: string;
  kind?: string;
  vendor?: string;
  model?: string;
  host?: string;
  port?: number;
  /** Discovery-time guess that this gateway needs a login (core
   *  `SetupSourceCandidate.requiresCredentials`) — the fallback input for
   *  {@link credentialsNeeded} before the boat's first harvest pass. */
  requiresCredentials?: boolean;
}

const VENDOR_LABELS: Record<string, string> = {
  actisense: 'Actisense',
  maretron: 'Maretron',
  yacht_devices: 'Yacht Devices',
  digital_yacht: 'Digital Yacht',
  garmin: 'Garmin',
  raymarine: 'Raymarine',
  furuno: 'Furuno',
  victron: 'Victron',
  navico: 'Navico',
  signalk: 'Signal K',
  generic: 'Gateway',
};

const KIND_LABELS: Record<string, string> = {
  signalk: 'Signal K server',
  nmea2000_udp: 'NMEA 2000 gateway',
  nmea2000_tcp: 'NMEA 2000 gateway',
  nmea0183_udp: 'NMEA 0183 source',
  nmea0183_tcp: 'NMEA 0183 source',
  usb_serial: 'USB gateway',
  vendor_web: 'Gateway',
  unknown: 'Gateway',
  // bridge_ftue_offer / ADR-078: the raw-N2K-over-Signal-K bridge candidate
  // discovered against a security-enabled Signal K server (see
  // isSecuredBridgeCandidate below).
  signalk_n2k_bridge: 'Raw N2K bridge',
};

/**
 * A human label for a discovered candidate, walking a fallback chain so a row is
 * never blank (task 7.2):
 * "Vendor Model" → Vendor → kind label → host → id.
 */
export function candidateLabel(c: RevealCandidate): string {
  const vendor = c.vendor ? VENDOR_LABELS[c.vendor] : undefined;
  if (vendor && c.model && c.model.trim() !== '') return `${vendor} ${c.model.trim()}`;
  if (c.model && c.model.trim() !== '') return c.model.trim();
  if (vendor) return vendor;
  if (c.kind && KIND_LABELS[c.kind]) return KIND_LABELS[c.kind]!;
  if (c.host && c.host.trim() !== '') return c.host.trim();
  if (c.id && c.id.trim() !== '') return c.id.trim();
  return 'A gateway';
}

/** A secondary detail line for a candidate ("192.168.1.10 · Signal K server"). */
export function candidateDetail(c: RevealCandidate): string {
  const parts: string[] = [];
  if (c.host && c.host.trim() !== '') {
    parts.push(c.port ? `${c.host}:${c.port}` : c.host);
  }
  if (c.kind && KIND_LABELS[c.kind]) parts.push(KIND_LABELS[c.kind]!);
  return parts.join(' · ');
}

// ---------- Alarm ordering (task 7.3 — deterministic floor above AI) ----------

export interface RevealAlarm {
  id: string;
  title: string;
  severity: string;
  defaultEnabled?: boolean;
  /** Set by the cloud AI layer; rendered with humility, never pre-checked. */
  aiSuggested?: boolean;
  confidence?: 'high' | 'medium' | 'low';
  explanation?: string;
}

const SEVERITY_RANK: Record<string, number> = {
  emergency: 0,
  alarm: 1,
  warning: 2,
  caution: 3,
};

/**
 * Wrong-guess humility (task 7.3): AI-suggested ids sort alphabetically ABOVE
 * deterministic recipe ids, so without re-ordering a default-off guess would
 * float above a critical safety alarm. Re-establish "deterministic safety floor
 * first, AI suggestions after", each group by severity. Stable, pure.
 */
export function orderAlarmsForDisplay<T extends RevealAlarm>(alarms: readonly T[]): T[] {
  return [...alarms].sort((a, b) => {
    const aAi = a.aiSuggested === true ? 1 : 0;
    const bAi = b.aiSuggested === true ? 1 : 0;
    if (aAi !== bAi) return aAi - bAi; // deterministic floor first
    const aSev = SEVERITY_RANK[a.severity] ?? 99;
    const bSev = SEVERITY_RANK[b.severity] ?? 99;
    if (aSev !== bSev) return aSev - bSev; // worst-first within a group
    return a.title.localeCompare(b.title);
  });
}

// ---------- Network health issues (FTUE↔cloud parity B2) ----------

/** Severity as emitted by the boat's network-health detectors (core
 *  `NetworkHealthSeverity`, `core/src/setup/types.ts`). */
export type HealthIssueSeverity = 'info' | 'warning' | 'critical';

/**
 * The reveal's slice of a boat-mirrored `NetworkHealthIssue` (core
 * `src/setup/types.ts`) — the fields the reveal renders. `affectedSourceIds` /
 * `affectedDeviceIds` / `evidence` are deliberately NOT mirrored: this is a
 * pure read-only render (correction stays on the existing gateway-pick
 * controls), so nothing here needs them.
 */
export interface RevealHealthIssue {
  id: string;
  severity: HealthIssueSeverity;
  category: string;
  title: string;
  recommendedAction: string;
  blocksAlarmRecommendation: boolean;
}

const HEALTH_SEVERITY_RANK: Record<HealthIssueSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

/**
 * Which health issues earn pixels on the reveal (B2): the reveal is a
 * celebration, so 'info' issues are dropped entirely — never rendered, never
 * counted. The survivors ('warning'/'critical') are ordered critical-first;
 * stable within a severity (Array#sort is stable), so arrival order from the
 * snapshot is otherwise preserved.
 *
 * `issues` is wire-sourced (mirrored verbatim from the boat snapshot,
 * `session.healthIssues` — `unknown[]` at the type boundary, see
 * `useBoatSetup.ts`), so a single malformed entry must be DROPPED here, not
 * thrown — mirrors the defensive per-item parsing already used for
 * `session.answers` above ({@link deriveAlarmDecisions}).
 */
export function visibleHealthIssues<T extends { severity: HealthIssueSeverity }>(
  issues: readonly T[]
): T[] {
  return issues
    .filter((i) => {
      if (typeof i !== 'object' || i === null) return false;
      const severity = (i as { severity?: unknown }).severity;
      return (
        typeof severity === 'string' && severity in HEALTH_SEVERITY_RANK && severity !== 'info'
      );
    })
    .sort((a, b) => HEALTH_SEVERITY_RANK[a.severity] - HEALTH_SEVERITY_RANK[b.severity]);
}

/**
 * The Alarms-stage blocker note (B2): the visible issues that suppressed an
 * alarm recommendation boat-side (`blocksAlarmRecommendation`), ordered like
 * {@link visibleHealthIssues}. `null` when there is nothing to explain, so
 * the stage renders no note at all — never an empty-state box for the common
 * (nothing blocked) case.
 */
export function alarmBlockerNote<T extends RevealHealthIssue>(issues: readonly T[]): T[] | null {
  const blocking = visibleHealthIssues(issues).filter((i) => i.blocksAlarmRecommendation);
  return blocking.length > 0 ? blocking : null;
}

// ---------- Per-alarm accept/reject decisions (reveal-alarm-review) ----------

/** One recorded per-recommendation decision, as sent to `setup/decisions`. */
export interface AlarmDecision {
  recommendationId: string;
  accepted: boolean;
}

/**
 * Derives the per-alarm accept/reject state a snapshot supports: the
 * deterministic floor defaults ACCEPTED, `aiSuggested` defaults REJECTED
 * (matching today's de-emphasis, contract §5.1 rule 1) — `defaultEnabled` is
 * NOT the seed. A persisted `recommendationChoice` answer (what the
 * setup_record_decisions verb writes boat-side) WINS over that default, so the map reflects the boat's durable
 * record on reload / re-reveal / the gateway-override re-flow (design A3),
 * never stale local state. `answers` is `session.answers` passed through
 * as-is (unknown shape on the wire); ids absent from `alarms` are ignored —
 * only the CURRENT recommendation set is ever surfaced.
 */
export function deriveAlarmDecisions(
  alarms: readonly RevealAlarm[],
  answers: readonly unknown[] | undefined
): Map<string, boolean> {
  const decisions = new Map<string, boolean>();
  for (const a of alarms) {
    decisions.set(a.id, a.aiSuggested !== true);
  }
  if (Array.isArray(answers)) {
    for (const raw of answers) {
      if (typeof raw !== 'object' || raw === null) continue;
      const entry = raw as { kind?: unknown; targetId?: unknown; value?: unknown };
      if (entry.kind !== 'recommendationChoice') continue;
      if (typeof entry.targetId !== 'string' || !decisions.has(entry.targetId)) continue;
      const value = entry.value;
      const decision =
        typeof value === 'object' && value !== null
          ? (value as { decision?: unknown }).decision
          : undefined;
      if (typeof decision !== 'string') continue;
      decisions.set(entry.targetId, decision === 'accepted');
    }
  }
  return decisions;
}

/**
 * Flattens the decision map into the `setup/decisions` wire payload —
 * completeness by construction (every `alarms[]` id was seeded into the map
 * by {@link deriveAlarmDecisions}, so this always covers the full set, never a
 * subset). Map iteration order is insertion order, so this is deterministic.
 */
export function buildDecisionsPayload(decisions: ReadonlyMap<string, boolean>): AlarmDecision[] {
  return Array.from(decisions, ([recommendationId, accepted]) => ({ recommendationId, accepted }));
}

/**
 * Drops entries from the reveal page's `decisionOverrides` staging layer once
 * they've round-tripped: a persisted `recommendationChoice` answer for an id
 * means {@link deriveAlarmDecisions} already reflects the boat's durable
 * record for it, so the override is redundant — and, more importantly, no
 * longer safe to keep. Left in place it would permanently pin the pre-toggle
 * value even after a LATER supersede (core `recordRecommendation` calls
 * `clearRecommendationDecision` under the same stable rec id when the alarm
 * materially changes), silently defeating that safety mechanism. Returns the
 * same `overrides` reference when nothing was pruned (setState no-op).
 */
export function pruneRoundTrippedOverrides(
  overrides: Map<string, boolean>,
  answers: readonly unknown[] | undefined
): Map<string, boolean> {
  if (overrides.size === 0 || !Array.isArray(answers)) return overrides;
  let next: Map<string, boolean> | null = null;
  for (const raw of answers) {
    if (typeof raw !== 'object' || raw === null) continue;
    const entry = raw as { kind?: unknown; targetId?: unknown };
    if (entry.kind !== 'recommendationChoice') continue;
    if (typeof entry.targetId !== 'string' || !overrides.has(entry.targetId)) continue;
    if (next === null) next = new Map(overrides);
    next.delete(entry.targetId);
  }
  return next ?? overrides;
}

// ---------- Credentials-needed (config-harvest inline path, B3) ----------

/**
 * A gateway/host the boat's config-harvest couldn't read without a device
 * login. Mirrors core's `ConfigHarvestNeedsCredentials`
 * (core/src/api/setup/config-harvest.ts route summary) VERBATIM — the
 * pre-agreed contract authority for this field's shape. Deliberately no
 * secret material: a target id, a best-effort host, and a vendor key only.
 */
export interface RevealCredentialsNeeded {
  gatewayId: string;
  host: string | null;
  vendor: string | null;
}

/**
 * Which gateways still need a login before config-harvest can read them
 * (B3). `needsCredentials` is `session.needsCredentials` passed through as-is
 * (unknown[] at the wire boundary, mirrored verbatim by the boat's inline
 * harvest — see `useBoatSetup.ts`), so once the field is PRESENT it is
 * authoritative — a malformed entry is DROPPED, not thrown, mirroring
 * {@link visibleHealthIssues}' per-item guard, but an authoritative empty
 * array (harvest ran, nothing outstanding) is NOT a signal to fall back.
 *
 * Falls back to `candidates[].requiresCredentials` only while
 * `needsCredentials` is absent entirely (no harvest summary has mirrored back
 * yet for this session) — a DISCOVERY-TIME GUESS from the scan probe, not the
 * harvest's actual verdict: it can read true for a gateway that turns out to
 * harvest fine passively, or miss one the harvest later flags. It exists only
 * to avoid a beat of "nothing needed" flashing before the first harvest
 * summary lands, and disappears the instant that summary arrives.
 */
export function credentialsNeeded(
  needsCredentials: unknown,
  candidates: readonly RevealCandidate[]
): RevealCredentialsNeeded[] {
  if (Array.isArray(needsCredentials)) {
    const out: RevealCredentialsNeeded[] = [];
    for (const raw of needsCredentials) {
      if (typeof raw !== 'object' || raw === null) continue;
      const entry = raw as { gatewayId?: unknown; host?: unknown; vendor?: unknown };
      if (typeof entry.gatewayId !== 'string' || entry.gatewayId.trim() === '') continue;
      out.push({
        gatewayId: entry.gatewayId,
        host: typeof entry.host === 'string' ? entry.host : null,
        vendor: typeof entry.vendor === 'string' ? entry.vendor : null,
      });
    }
    return out;
  }
  return candidates
    .filter((c) => c.requiresCredentials === true && typeof c.id === 'string' && c.id.trim() !== '')
    .map((c) => ({ gatewayId: c.id!, host: c.host ?? null, vendor: c.vendor ?? null }));
}

// ---------- Secured-bridge opt-in offer (bridge_ftue_offer, ADR-078) ----------
//
// A security-enabled Signal K server 401/403s the unauthenticated bridge health
// probe, so core's auto-create-sources.ts never silently creates the bridge —
// it only promotes `requiresApproval` to the candidate's `requiresCredentials`
// flag (fixed wire contract 1+2). The reveal must therefore present it as an
// explicit, opt-in offer (default OFF) rather than staying silent; accepting
// folds the candidateId into the staged plan's `createCandidates` (see
// lib/networkRecommendation.ts buildApplyDecisions), which the boat resolves
// and creates BEFORE applying the plan's roles.

/**
 * Contract 2: a discovered candidate is a "secured bridge" opt-in offer when
 * it is a `signalk_n2k_bridge` kind carrying `requiresCredentials` — the
 * signal core's `probeBridgeHealth` sets on a 401/403'd health probe.
 */
export function isSecuredBridgeCandidate(c: RevealCandidate): boolean {
  return c.kind === 'signalk_n2k_bridge' && c.requiresCredentials === true;
}

/**
 * The secured-bridge opt-in offers present in the discovered candidate list.
 * These are never auto-created boat-side, so the reveal's gateway beat must
 * surface them explicitly. Filters out any entry with no usable id (nothing
 * to opt into / relay as a `createCandidates` entry). Ordinarily 0 or 1 entry
 * — a boat has at most one paired bridge per secured Signal K server — but
 * this doesn't assume that.
 */
export function securedBridgeOffers(candidates: readonly RevealCandidate[]): RevealCandidate[] {
  return candidates.filter(
    (c) => isSecuredBridgeCandidate(c) && typeof c.id === 'string' && c.id.trim() !== ''
  );
}

/**
 * Mirrors core's `SetupSourceStatus` (contract 3) — a DERIVED, in-memory live
 * source status merged onto the session snapshot at read time, never
 * persisted. `session.sourceStatuses[]` is unknown-shape at the wire boundary
 * like the other passthrough snapshot fields, so it's parsed defensively
 * below rather than trusted.
 */
export interface RevealSourceStatus {
  id: string;
  type: string;
  state: 'connecting' | 'connected' | 'disconnected' | 'error';
  reason?: string;
}

const SOURCE_STATUS_STATES = new Set(['connecting', 'connected', 'disconnected', 'error']);

/** Defensive per-item parse of `session.sourceStatuses` — a malformed entry is
 *  DROPPED, not thrown, mirroring {@link visibleHealthIssues} / {@link credentialsNeeded}.
 *  `session` is the reveal's loosely-typed passthrough snapshot (`[key: string]: unknown`). */
function parseSourceStatuses(session: unknown): RevealSourceStatus[] {
  if (typeof session !== 'object' || session === null) return [];
  const raw = (session as { sourceStatuses?: unknown }).sourceStatuses;
  if (!Array.isArray(raw)) return [];
  const out: RevealSourceStatus[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const entry = item as { id?: unknown; type?: unknown; state?: unknown; reason?: unknown };
    if (typeof entry.id !== 'string' || entry.id.trim() === '') continue;
    if (typeof entry.type !== 'string' || entry.type.trim() === '') continue;
    if (typeof entry.state !== 'string' || !SOURCE_STATUS_STATES.has(entry.state)) continue;
    out.push({
      id: entry.id,
      type: entry.type,
      state: entry.state as RevealSourceStatus['state'],
      ...(typeof entry.reason === 'string' ? { reason: entry.reason } : {}),
    });
  }
  return out;
}

/**
 * Contract 5: the reveal's "waiting for Signal K admin approval" sub-beat —
 * the FIRST `signalk_n2k_bridge` source in `state: 'connecting'` whose reason
 * names the approval step (core's `PENDING_APPROVAL_REASON`, e.g. "Approve
 * 'Neomaris Core' in Signal K admin → Security → Access Requests"). `null` when
 * nothing is currently awaiting approval, so {@link RevealSignalKAccess}
 * renders nothing.
 */
export function bridgePendingApproval(session: unknown): RevealSourceStatus | null {
  return (
    parseSourceStatuses(session).find(
      (s) =>
        s.type === 'signalk_n2k_bridge' &&
        s.state === 'connecting' &&
        typeof s.reason === 'string' &&
        s.reason.includes('Approve')
    ) ?? null
  );
}

/**
 * Contract 5: has the offered bridge connected? Drives the waiting sub-beat's
 * auto-advance — once true, {@link bridgePendingApproval} also naturally
 * returns null (the source is no longer `connecting`), so the sub-beat
 * disappears on its own; callers that want to react to the transition (e.g.
 * tearing down a poll interval) can watch this directly.
 */
export function bridgeConnected(session: unknown): boolean {
  return parseSourceStatuses(session).some(
    (s) => s.type === 'signalk_n2k_bridge' && s.state === 'connected'
  );
}
